<template>
  <div class="map-search">
    <v-autocomplete
      ref="input"
      v-model="selected"
      :items="results"
      :search-input.sync="query"
      :loading="loading"
      item-text="label"
      item-value="id"
      return-object
      no-filter
      dense
      outlined
      hide-details
      hide-no-data
      clearable
      :placeholder="placeholder"
      prepend-inner-icon="mdi-magnify"
      append-icon=""
      class="search-input"
      @change="onSelect"
      @focus="$emit('focus-change', true)"
      @blur="$emit('focus-change', false)"
    >
      <template #item="{ item }">
        <v-list-item-icon class="mr-2">
          <v-icon small :color="item.color || ''">{{ item.icon || 'mdi-map-marker' }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ item.label }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.subtitle">{{ item.subtitle }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action
          v-if="radioEnabled && isTetraDevice(item)"
          class="ml-1 my-0 search-tetra-action"
        >
          <v-btn
            icon
            x-small
            v-auto-tooltip="'Habla por radio TETRA'"
            class="search-tetra-radio-btn"
            @click.stop="onRadioCall(item)"
          >
            <v-icon x-small color="var(--kln-accent)">mdi-radio-handheld</v-icon>
          </v-btn>
        </v-list-item-action>
      </template>

      <!-- Toggle del filtro geo (solo si env tiene estados configurados).
           Activo: pin accent con los estados del env; inactivo: globo gris
           = búsqueda en todo México. Se muestra siempre a la derecha del
           input, no desaparece con el clearable. -->
      <template v-if="placesFilterAvailable" #append>
        <v-icon
          small
          class="places-filter-toggle"
          :class="{ 'places-filter-toggle--active': placesFilterActive }"
          :color="placesFilterActive ? 'var(--kln-accent)' : 'rgba(255,255,255,0.45)'"
          v-auto-tooltip="placesFilterTip"
          @click.stop="togglePlacesFilter"
        >
          {{ placesFilterActive ? 'mdi-map-marker-radius' : 'mdi-earth' }}
        </v-icon>
      </template>
    </v-autocomplete>

    <!-- El panel de Búsqueda Inteligente ya no se monta como v-dialog aquí:
         vive en el leftstack de MapV2.vue (SmartSearchPanel). Cuando el
         usuario elige la opción "ai-search" del dropdown, dispatchamos el
         action openSmartSearch que pre-carga la query y abre el panel
         (colapsando los demás del leftstack por ser exclusivo). -->
  </div>
</template>

<script>
import googlePlacesService from '@/util/googlePlaces.service.js'
import { ENTITY_CONFIGS } from '../data/entity-configs.js'
import { isRadioDevice } from '../data/radio-device.js'
import {
  ensureDriverDirectory, isDriverDirectoryLoaded, getDriverNameForRaw, getDriverHaystackForRaw, normalizeText
} from '../data/driver-directory.js'

var searchDebounce = null

// Estados pre-configurados para filtrar la búsqueda de lugares (Google
// Places). El operador puede apagar el filtro desde el ícono del input
// para buscar en todo México. Vacío = sin toggle (no hay nada que filtrar).
var DEFAULT_PLACES_ESTADOS = (function() {
  var raw = import.meta.env.VITE_PLACES_DEFAULT_ESTADOS || ''
  return raw.split(',').map(function(s) { return s.trim().toUpperCase() }).filter(Boolean)
})()

export default {
  name: 'MapSearch',

  props: {
    monitors: { type: Object, default: function() { return {} } },
    map: { type: Object, default: null },
    // Placeholder dinámico: el parent usa uno corto ("Buscar...") cuando el
    // overlay está colapsado y uno descriptivo cuando está expandido.
    placeholder: { type: String, default: 'Buscar unidades, cámaras, lugares...' },
    // Botón "Habla por radio" por fila; lo apaga MapV2 si el perfil no da Radio TETRA.
    radioEnabled: { type: Boolean, default: true },
    // Opción «búsqueda inteligente» (sus resultados son cámaras); la apaga MapV2 si el perfil no ve cámaras.
    aiSearchEnabled: { type: Boolean, default: true },
  },

  data: function() {
    return {
      query: '',
      selected: null,
      results: [],
      loading: false,
      // Toggle del filtro geo de Google Places. Activo por default si
      // hay estados definidos en el env; se puede apagar en vivo desde
      // el ícono del input para buscar en todo México.
      placesFilterActive: DEFAULT_PLACES_ESTADOS.length > 0,
    }
  },

  computed: {
    // Solo mostramos el toggle si hay algún estado configurado en env.
    // Sin env, la búsqueda de lugares es global por naturaleza y el
    // toggle sería inútil.
    placesFilterAvailable() {
      return DEFAULT_PLACES_ESTADOS.length > 0
    },

    placesFilterLabels() {
      return DEFAULT_PLACES_ESTADOS.join(', ')
    },

    placesFilterTip() {
      if (!this.placesFilterActive) return 'Buscando en todo México · click para filtrar a ' + this.placesFilterLabels
      return 'Filtrando a ' + this.placesFilterLabels + ' · click para buscar en todo México'
    },
  },

  watch: {
    query: function(val) {
      if (!val || val.length < 2) { this.results = []; return }
      // Carga lazy del directorio de elementos al primer uso real de la
      // búsqueda. Cuando resuelve, re-corremos la query activa para que
      // los matches por nombre de persona aparezcan sin re-teclear. Una
      // vez cargado ya no re-agendamos (evita doble search por keystroke).
      if (!isDriverDirectoryLoaded()) {
        var self = this
        ensureDriverDirectory().then(function() {
          if (self.query && self.query.length >= 2) self.search(self.query)
        })
      }
      this.search(val)
    },
  },

  methods: {
    search: function(q) {
      var self = this
      // Normalizado sin acentos: "gonzález" matchea "GONZALEZ" y viceversa,
      // tanto en nombres de device como en nombres de elemento.
      var lower = normalizeText(q)

      // 1. Local monitor search — each word is a partial substring, ALL must match
      var words = lower.trim().split(/\s+/).filter(function(w) { return w.length > 0 })
      if (!words.length) { this.results = []; return }
      var localResults = []
      var keys = Object.keys(this.monitors)
      for (var i = 0; i < keys.length && localResults.length < 8; i++) {
        var m = this.monitors[keys[i]]
        var haystack = normalizeText((m.title || '') + ' ' + (m.data ? (m.data.uniqueId || '') + ' ' + (m.data.name || '') + ' ' + (m.data.identifier || '') + ' ' + (m.data.id || '') : ''))
        if (m.entityType === 'device') {
          haystack += ' ' + getDriverHaystackForRaw(m.data)
        }
        var allMatch = true
        for (var w = 0; w < words.length; w++) {
          if (haystack.indexOf(words[w]) === -1) { allMatch = false; break }
        }
        if (allMatch) {
          // Para devices con elemento conocido, el subtítulo muestra a la
          // persona — el operador ve por qué matcheó y quién porta el equipo.
          var driverName = m.entityType === 'device' ? getDriverNameForRaw(m.data) : ''
          localResults.push({
            id: m.id,
            label: m.title,
            subtitle: driverName || (m.entityType === 'lpr' && m.data && m.data.identifier ? 'lpr · ' + m.data.identifier : m.entityType),
            icon: this.getEntityIcon(m.entityType),
            color: m.color,
            type: 'monitor',
            data: m,
          })
        }
      }

      this.results = localResults.slice()

      // 2. Google Places (debounced, >5 chars)
      if (q.length > 5) {
        clearTimeout(searchDebounce)
        this.loading = true
        var statesForPlaces = self.placesFilterActive ? DEFAULT_PLACES_ESTADOS : null
        searchDebounce = setTimeout(function() {
          googlePlacesService.searchPlaces(q, 5, 5, statesForPlaces).then(function(places) {
            var placeResults = (places || []).slice(0, 5).map(function(p) {
              return {
                id: 'place-' + p.placeId,
                label: p.name,
                subtitle: 'Dirección',
                icon: 'mdi-map-marker',
                color: '#2196F3',
                type: 'place',
                data: p,
              }
            })
            self.results = localResults.concat(placeResults)

            // 3. AI Search option at the end
            if (self.aiSearchEnabled) {
              self.results.push({
                id: 'ai-search',
                label: q + '... búsqueda inteligente',
                subtitle: 'Búsqueda con IA en cámaras',
                icon: 'mdi-creation',
                color: 'var(--kln-accent)',
                type: 'ai-search',
              })
            }

            self.loading = false
          }).catch(function() {
            self.loading = false
          })
        }, 750)
      } else if (this.aiSearchEnabled) {
        // Add AI search option even without places
        this.results.push({
          id: 'ai-search',
          label: q + '... búsqueda inteligente',
          subtitle: 'Búsqueda con IA en cámaras',
          icon: 'mdi-creation',
          color: 'var(--kln-accent)',
          type: 'ai-search',
        })
      }
    },

    onSelect: function(item) {
      if (!item) return
      this.selected = null // reset for next search

      if (item.type === 'ai-search') {
        // Dispara el panel SmartSearch del leftstack (exclusivo). La action
        // setea la query y abre el panel en un solo commit.
        this.$store.dispatch('map-v2/openSmartSearch', this.query || '')
        this.query = ''
        return
      }

      if (item.type === 'place') {
        var self = this
        googlePlacesService.getPlaceDetails(item.data.placeId).then(function(place) {
          if (place && place.geometry && place.geometry.location) {
            var lat = place.geometry.location.lat()
            var lng = place.geometry.location.lng()
            self.$emit('select-place', [lng, lat])
          }
        }).catch(function() {})
        this.query = ''
        return
      }

      if (item.type === 'monitor') {
        this.$emit('select-monitor', item.data)
        this.query = ''
        return
      }
    },

    getEntityIcon: function(type) {
      var cfg = ENTITY_CONFIGS[type]
      return cfg ? cfg.mdiIcon : 'mdi-map-marker'
    },

    // Discriminador de radio — device cuya categoria es de tipo radio (tetra|mcx).
    // El item viene del autocomplete; item.data es el MapMonitor y
    // item.data.data es el rawDevice donde vive categoryData.
    isTetraDevice: function(item) {
      if (!item || item.type !== 'monitor') return false
      var monitor = item.data
      if (!monitor || monitor.entityType !== 'device') return false
      var raw = monitor.data || monitor
      return isRadioDevice(raw)
    },

    onRadioCall: function(item) {
      // Emite el MapMonitor entero — MapV2.onRadioCallFromSearch lo
      // normaliza y abre el panel TETRA apuntando a este device. NO
      // limpiamos la query para que el operador pueda agregar más
      // devices al canal sin re-buscar (si el panel ya está abierto, el
      // diálogo "cambiar/agregar" se ocupa del flujo).
      if (item && item.data) {
        this.$emit('radio-call', item.data)
      }
    },

    togglePlacesFilter: function() {
      this.placesFilterActive = !this.placesFilterActive
      // Re-disparar la búsqueda con la nueva configuración si hay query.
      if (this.query && this.query.length >= 2) this.search(this.query)
    },

    // Método público invocado desde MapV2 cuando el usuario clickea la
    // barra colapsada (el overlay entero es clickeable; focus al input
    // dispara la expansión via @focus-change). v-autocomplete expone
    // focus() en su instancia.
    focus: function() {
      if (this.$refs.input && typeof this.$refs.input.focus === 'function') {
        this.$refs.input.focus()
      }
    },
  },
}
</script>

<style scoped>
.map-search {
  flex: 1;
  min-width: 200px;
}

.search-input {
  font-size: 13px !important;
}

/* Toggle del filtro geo — visible solo cuando hay estados en env. */
.places-filter-toggle {
  cursor: pointer;
  transition: transform 0.12s, opacity 0.15s;
}

.places-filter-toggle:hover {
  transform: scale(1.12);
  opacity: 1;
}

/* Botón radio TETRA por fila — visible solo al hover de la fila para
   no saturar el dropdown cuando hay muchos resultados. */
.search-tetra-action {
  align-self: center;
}
.search-tetra-radio-btn {
  opacity: 0;
  transition: opacity 0.15s, background 0.15s;
}
.search-tetra-radio-btn:hover {
  background: var(--kln-accent-soft, rgba(22, 196, 199, 0.15)) !important;
}
</style>

<!-- Estilos globales (sin scoped) para acceder al list-item del menú del
     autocomplete, que se renderiza fuera del scope CSS del componente. -->
<style>
.v-list-item:hover .search-tetra-radio-btn,
.v-list-item--highlighted .search-tetra-radio-btn,
.v-list-item--active .search-tetra-radio-btn {
  opacity: 1;
}
</style>
